import React, { useState } from "react";
import YhModal from "../../../../ui/YhModal";
import YhInput from "../../../../ui/YhInput";
import YhButton from "../../../../ui/YhButton";
import {
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "../../../../helpers/methods";
import notifiers from "../../../../global/constants/NotificationConstants";
import customErrorHandler from "../../../../utils/customErrorHandler";
import { AddLoanApi } from "./AddLoanServices";

interface CustomProps {
  isDialogOpenLoan: boolean;
  handleCloseLoan: Function;
  userId: string;
  status: any;
}

const emptyForm = {
  loan_acc: "",
  loan_amt: "",
  loan_emi: "",
  start_from: "",
  updated_amt: "",
  status: "",
};

const AddLoan = (props: CustomProps) => {
  const [saving, setSaving] = useState(false);
  const [formValues, setFormValues] = useState({ ...emptyForm });
  const [validationErrors, setValidationErrors] = useState({
    loan_acc: "",
    loan_amt: "",
    loan_emi: "",
    start_from: "",
    updated_amt: "",
  });

  const resetForm = () => {
    setFormValues({ ...emptyForm });
    setValidationErrors({
      loan_acc: "",
      loan_amt: "",
      loan_emi: "",
      start_from: "",
      updated_amt: "",
    });
  };

  const handleClose = () => {
    resetForm();
    props.handleCloseLoan();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    setValidationErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const isFormValid = () => {
    const errors = {
      loan_acc: "",
      loan_amt: "",
      loan_emi: "",
      start_from: "",
      updated_amt: "",
    };
    let isValid = true;

    if (!formValues.loan_acc.trim()) {
      errors.loan_acc = "Please enter loan account.";
      isValid = false;
    }
    if (!formValues.loan_amt.trim()) {
      errors.loan_amt = "Please enter loan amount.";
      isValid = false;
    }
    if (!formValues.loan_emi.trim()) {
      errors.loan_emi = "Please enter loan EMI.";
      isValid = false;
    }
    if (!formValues.start_from.trim()) {
      errors.start_from = "Please enter start date.";
      isValid = false;
    }
    if (!formValues.updated_amt.trim()) {
      errors.updated_amt = "Please enter updated amount.";
      isValid = false;
    }

    setValidationErrors(errors);
    return isValid;
  };

  const handleFormSubmit = async () => {
    if (!isFormValid()) return;

    try {
      setSaving(true);
      const response = await AddLoanApi({
        ...formValues,
        status: props?.status,
        userId: props?.userId,
      });
      customErrorHandler(response);
      if (response?.success === 1) {
        openSuccessNotification(response?.message);
      }
      handleClose();
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error?.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <YhModal
      open={!!props.isDialogOpenLoan}
      onClose={handleClose}
      width={560}
      title="Add Loan"
      closable
      footer={
        <div className="flex justify-end gap-2">
          <YhButton variant="secondary" size="sm" onClick={handleClose}>
            Cancel
          </YhButton>
          <YhButton
            variant="primary"
            size="sm"
            loading={saving}
            onClick={handleFormSubmit}
          >
            Submit
          </YhButton>
        </div>
      }
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <YhInput
          label="Loan Account"
          name="loan_acc"
          placeholder="Loan Account"
          value={formValues.loan_acc}
          onChange={handleInputChange}
          error={validationErrors.loan_acc}
        />
        <YhInput
          label="Loan Amount"
          name="loan_amt"
          type="number"
          placeholder="Loan Amount"
          value={formValues.loan_amt}
          onChange={handleInputChange}
          error={validationErrors.loan_amt}
        />
        <YhInput
          label="Loan EMI"
          name="loan_emi"
          type="number"
          placeholder="Loan EMI"
          value={formValues.loan_emi}
          onChange={handleInputChange}
          error={validationErrors.loan_emi}
        />
        <YhInput
          label="Start from"
          name="start_from"
          type="date"
          placeholder="dd/mm/yyyy"
          value={formValues.start_from}
          onChange={handleInputChange}
          error={validationErrors.start_from}
        />
        <YhInput
          label="Updated amount"
          name="updated_amt"
          type="number"
          placeholder="Updated amount"
          value={formValues.updated_amt}
          onChange={handleInputChange}
          error={validationErrors.updated_amt}
          containerClassName="sm:col-span-2"
        />
      </div>
    </YhModal>
  );
};

export default AddLoan;
